import { Box, Container, Typography, LinearProgress } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import CodeIcon from '@mui/icons-material/Code';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const stats = [
  {
    value: 'Founder-Led',
    label: 'Independent Studio',
    icon: <CodeIcon />,
    color: '#7C3AED',
  },
  {
    value: '1:1',
    label: 'Direct Collaboration',
    icon: <EmojiEventsIcon />,
    color: '#F59E0B',
  },
  {
    value: '10+ Years',
    label: 'Experience',
    icon: <DevicesIcon />,
    color: '#06B6D4',
  },
  {
    value: 'Now Booking',
    label: 'Partners',
    icon: <SpeedIcon />,
    color: '#10B981',
  },
];

const skills = [
  { label: 'React / Next.js', level: 92, color: '#7C3AED' },
  { label: 'Node.js / Express', level: 80, color: '#06B6D4' },
  { label: 'UI/UX Design', level: 85, color: '#F59E0B' },
  { label: 'SEO & Performance', level: 88, color: '#10B981' },
];

function AnimatedBar({ level, color }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <Box ref={ref}>
      <LinearProgress
        variant='determinate'
        value={inView ? level : 0}
        sx={{
          height: 6,
          borderRadius: 3,
          background: 'rgba(255,255,255,0.07)',
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${color}, ${color}80)`,
            borderRadius: 3,
            transition: 'transform 1.5s ease-in-out',
          },
        }}
      />
    </Box>
  );
}

export default function About() {
  return (
    <Box
      id='about'
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #0F0F1A 0%, #12101E 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background accents */}
      <Box
        sx={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: '#06B6D4',
          filter: 'blur(150px)',
          opacity: 0.05,
          top: '20%',
          right: '-10%',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: '#7C3AED',
          filter: 'blur(130px)',
          opacity: 0.06,
          bottom: '-10%',
          left: '-10%',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth='lg'>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Box
              sx={{
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: 50,
                background: 'rgba(6,182,212,0.12)',
                border: '1px solid rgba(6,182,212,0.3)',
                mb: 2,
              }}
            >
              <Typography
                variant='overline'
                sx={{ color: '#22D3EE', fontWeight: 700, letterSpacing: 2 }}
              >
                About
              </Typography>
            </Box>
            <Typography
              variant='h2'
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                mb: 2,
              }}
            >
              Meet the{' '}
              <Box component='span' className='gradient-text'>
                Developer
              </Box>
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={6} alignItems='center'>
          {/* Left - Bio */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
            >
              {/* Avatar */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow:
                      '0 0 0 4px rgba(124,58,237,0.2), 0 0 0 8px rgba(124,58,237,0.08)',
                    fontSize: '2.5rem',
                  }}
                >
                  <Typography
                    variant='h3'
                    sx={{ fontFamily: 'Poppins', fontWeight: 800 }}
                  >
                    D
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant='h5'
                    sx={{ fontFamily: 'Poppins', fontWeight: 700 }}
                  >
                    Dan
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: '#A78BFA', fontWeight: 600, mt: 0.5 }}
                  >
                    Freelance Full-Stack Developer
                  </Typography>
                  <Box
                    sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}
                  >
                    {['React', 'Node.js', 'UI/UX'].map((tag) => (
                      <Box
                        key={tag}
                        sx={{
                          px: 1.25,
                          py: 0.3,
                          borderRadius: 50,
                          background: 'rgba(124,58,237,0.15)',
                          border: '1px solid rgba(124,58,237,0.3)',
                        }}
                      >
                        <Typography
                          variant='caption'
                          sx={{
                            color: '#C4B5FD',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        >
                          {tag}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Typography
                variant='body1'
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.9,
                  mb: 2.5,
                  fontSize: '0.98rem',
                }}
              >
                I&apos;m a passionate freelance web developer with a sharp eye
                for design and an obsession with writing clean, performant code.
                I specialise in building bespoke websites and applications for
                small businesses that need a serious digital upgrade.
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.9,
                  mb: 4,
                  fontSize: '0.98rem',
                }}
              >
                Whether you need a polished landing page that converts or a
                complex booking system — I deliver on time, on budget, and
                beyond expectations. Every pixel is intentional. Every
                interaction is crafted.
              </Typography>

              {/* Skills */}
              <Box>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2.5, fontFamily: 'Poppins' }}
                >
                  Core Skills
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {skills.map((skill) => (
                    <Box key={skill.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 600,
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '0.88rem',
                          }}
                        >
                          {skill.label}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: skill.color,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                          }}
                        >
                          {skill.level}%
                        </Typography>
                      </Box>
                      <AnimatedBar level={skill.level} color={skill.color} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Right - Stats */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
            >
              <Grid container spacing={3}>
                {stats.map((stat, i) => (
                  <Grid key={stat.label} size={{ xs: 6 }}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                    >
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: '18px',
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          backdropFilter: 'blur(10px)',
                          textAlign: 'center',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            border: `1px solid ${stat.color}40`,
                            boxShadow: `0 10px 30px ${stat.color}15`,
                            transform: 'translateY(-4px)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: `${stat.color}18`,
                            border: `1px solid ${stat.color}30`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            color: stat.color,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          variant='h2'
                          sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 900,
                            fontSize: { xs: '1.15rem', md: '1.55rem' },
                            color: stat.color,
                            lineHeight: 1.1,
                            minHeight: { xs: 38, md: 44 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'rgba(255,255,255,0.5)',
                            mt: 0.75,
                            fontWeight: 500,
                            fontSize: '0.82rem',
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              {/* Decorative quote card */}
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  borderRadius: '18px',
                  background:
                    'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(6,182,212,0.08))',
                  border: '1px solid rgba(124,58,237,0.2)',
                  backdropFilter: 'blur(10px)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(124,58,237,0.08)',
                    pointerEvents: 'none',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontStyle: 'italic',
                    color: 'rgba(255,255,255,0.75)',
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  &ldquo;I believe every small business deserves a website as
                  ambitious as their dreams. Great design isn&apos;t a luxury —
                  it&apos;s your most powerful sales tool.&rdquo;
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 2,
                      background: 'linear-gradient(90deg, #7C3AED, #06B6D4)',
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    variant='caption'
                    sx={{ color: '#A78BFA', fontWeight: 600 }}
                  >
                    Dan, Get Your Site Today
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
